import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, XCircle, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import type { Order } from "@/types/order";

export function OrderDetailPage() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => get<Order>(`/orders/orders/${orderId}`),
    enabled: !!orderId,
  });

  const cancel = useMutation({
    mutationFn: () => post<Order>(`/orders/orders/${orderId}/cancel`, { reason: "customer-cancelled" }),
    onSuccess: () => {
      toast.success("Order cancelled successfully");
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders-list"] });
    },
  });

  if (isLoading || !data) {
    return <Skeleton className="h-96 w-full rounded-xl" />;
  }

  const canCancel = data.status === "pending_payment" || data.status === "payment_verification_pending";

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-surface-500 hover:text-brand-600 inline-flex items-center gap-1 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Orders
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-2xs uppercase tracking-wide text-surface-500 font-semibold">Order Details</div>
          <h1 className="text-2xl font-bold text-surface-900 font-mono tracking-tight">{data.order_id}</h1>
          <div className="mt-2 flex items-center gap-3 bg-white p-2 rounded-lg border border-surface-200 shadow-sm w-fit">
            <div>
              <span className="text-2xs text-surface-400 block mb-0.5">Order Status</span>
              <StatusPill status={data.status} />
            </div>
            <div className="w-px h-8 bg-surface-200" />
            <div>
              <span className="text-2xs text-surface-400 block mb-0.5">Payment</span>
              <StatusPill status={data.payment_status} />
            </div>
            <div className="w-px h-8 bg-surface-200" />
            <div>
              <span className="text-2xs text-surface-400 block mb-0.5">Delivery</span>
              <StatusPill status={data.shipping_status} />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canCancel && (
            <Button variant="danger" leftIcon={<XCircle className="h-4 w-4" />}
                    loading={cancel.isPending} onClick={() => {
                      if(window.confirm("Are you sure you want to cancel this order?")) {
                        cancel.mutate();
                      }
                    }}>
              Cancel Order
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT: Products Table */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50 overflow-hidden">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Products Ordered</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Product</TH>
                    <TH>SKU</TH>
                    <TH className="text-center">Qty</TH>
                    <TH className="text-right">Line Total</TH>
                  </TR>
                </THead>
                <tbody>
                  {data.items.map((i) => (
                    <TR key={i.id}>
                      <TD className="font-semibold text-surface-900">{i.product_name}</TD>
                      <TD className="font-mono text-xs text-surface-500">{i.variant_sku}</TD>
                      <TD className="text-center font-bold">{i.quantity}</TD>
                      <TD className="text-right font-extrabold text-brand-700">{formatCurrency(i.line_total)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
              <div className="p-5 bg-surface-50 flex justify-between items-center border-t border-surface-200">
                <span className="text-sm font-bold uppercase tracking-wider text-surface-500">Total Order Amount</span>
                <span className="text-2xl font-extrabold text-brand-700 tracking-tight">{formatCurrency(data.total)}</span>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* RIGHT: Summary & Shipping Details */}
        <div className="space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <SummaryRow label="Subtotal" value={formatCurrency(data.subtotal)} />
              <SummaryRow label="Total"    value={formatCurrency(data.total)} highlight />
              <div className="divider" />
              <SummaryRow label="Placed At"    value={formatDateTime(data.placed_at)} />
              {data.completed_at && <SummaryRow label="Completed" value={formatDateTime(data.completed_at)} />}
              {data.cancelled_at && <SummaryRow label="Cancelled" value={formatDateTime(data.cancelled_at)} />}
              {data.rejected_at  && <SummaryRow label="Rejected"  value={formatDateTime(data.rejected_at)} />}
              
              <div className="divider" />
              <div className="pt-2 flex items-center gap-2 text-2xs text-surface-400">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span className="font-mono">Corr ID: {data.correlation_id.slice(0, 8)}…</span>
              </div>
            </CardBody>
          </Card>

          {data.notes && (
            <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50 bg-brand-50">
              <CardHeader className="border-b border-brand-200/50">
                <CardTitle>
                  <MapPin className="w-4 h-4 text-brand-600" /> Shipping Information
                </CardTitle>
              </CardHeader>
              <CardBody className="text-sm text-brand-900 whitespace-pre-wrap leading-relaxed">
                {data.notes.replace("Shipping Address: ", "")}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-surface-600">{label}</span>
      <span className={highlight ? "text-lg font-bold text-surface-900" : "text-sm font-medium text-surface-800"}>
        {value}
      </span>
    </div>
  );
}